
import React, { useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { PrayerResult as PrayerResultType } from "@/hooks/prayer/usePrayerGeneration";
import { useScrollToNewContent } from "@/hooks/useScrollToNewContent";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PrayerVoice } from "@/hooks/prayer/usePrayerVoices";
import { useAzurePrayerAudio } from "@/hooks/prayer/useAzurePrayerAudio";
import type { Json } from "@/integrations/supabase/types";
import PrayerHeavenlyLoader from "@/components/PrayerHeavenlyLoader";

// Import our sub-components
import PrayerText from "./prayer/PrayerText";
import ScriptureReferences from "./prayer/ScriptureReferences";
import AudioControls from "./prayer/AudioControls";
import PrayerActions from "./prayer/PrayerActions";

interface PrayerResultProps {
  result: PrayerResultType;
  selectedVoice: PrayerVoice;
}

const PrayerResult: React.FC<PrayerResultProps> = ({
  result,
  selectedVoice
}) => {
  // Toast notifications
  const { toast } = useToast();
  
  // Reference to the download link
  const downloadLinkRef = useRef<HTMLAnchorElement | null>(null);
  
  // Scroll to this component when it mounts
  const scrollRef = useScrollToNewContent(result, {
    delay: 300
  });

  // Use the Azure prayer audio hook with improved error handling
  const { 
    isGeneratingAudio, 
    isPlaying,
    isAudioReady,
    error: audioError,
    loadingProgress, // New prop for loading progress
    isIOS, // New prop for iOS detection
    generateAndPlayAudio, 
    togglePlayback,
    cleanupAudio,
    audioUrlRef 
  } = useAzurePrayerAudio();

  // Clean up audio when component unmounts
  useEffect(() => {
    return cleanupAudio;
  }, [cleanupAudio]);

  // Calculate if prayer is longer than audio limit
  const isPrayerTooLong = result.prayer.length > 4000;

  // Handle play button click
  const handlePlayPrayer = () => {
    if (isPlaying) {
      togglePlayback();
    } else if (isAudioReady) {
      togglePlayback();
    } else {
      // Only generate audio if not already generated
      generateAndPlayAudio(result.prayer, selectedVoice);
    }
  };

  // Handle save button click with proper error handling
  const handleSavePrayer = async () => {
    try {
      // Check if user is logged in
      const { data: session } = await supabase.auth.getSession();
      
      if (!session?.session) {
        toast({
          title: "Authentication required",
          description: "Please sign in to save prayers to your dashboard",
          variant: "destructive"
        });
        return;
      }
      
      // Prepare data for saving
      const scripturesJson = result.scriptures.map(scripture => ({
        reference: scripture.reference,
        text: scripture.text
      }));
      
      // Updated: Include the full prayer text in a more accessible format for dashboard display
      const prayerToSave = {
        prayer: result.prayer,
        scriptures: scripturesJson,
        voiceId: selectedVoice.id,
        voiceName: selectedVoice.name,
        savedAt: new Date().toISOString(),
        // Add a dedicated displayText field for easier dashboard preview
        displayText: result.prayer.substring(0, 300) + (result.prayer.length > 300 ? '...' : '')
      };
      
      // Save to Supabase with error handling
      const { error } = await supabase.from('saved_responses').insert({
        user_id: session.session.user.id,
        tool_type: 'prayer', // Set the correct tool type for dashboard filtering
        title: `Prayer: ${result.prayer.substring(0, 50)}...`,
        content: prayerToSave as Json
      });
      
      if (error) {
        console.error("Error saving prayer:", error);
        toast({
          title: "Failed to save prayer",
          description: error.message,
          variant: "destructive"
        });
        return;
      }
      
      toast({
        title: "Prayer saved to dashboard",
        description: "Your prayer has been saved to your personal dashboard",
        duration: 5000
      });
    } catch (error) {
      console.error("Error saving prayer:", error);
      toast({
        title: "Failed to save prayer",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
    }
  };

  // Handle download button click with improved error prevention
  const handleDownloadAudio = async () => {
    // Must have audio ready to download
    if (!isAudioReady && !audioUrlRef.current) {
      toast({
        title: "Generating audio for download",
        description: "Please wait while we prepare your audio file",
      });
      
      try {
        // Generate audio first, then proceed with download after
        await generateAndPlayAudio(result.prayer, selectedVoice);
        
        // Audio should be generated now, check if it's available
        setTimeout(() => {
          if (audioUrlRef.current && downloadLinkRef.current && !audioError) {
            downloadLinkRef.current.download = `Prayer-${new Date().toISOString().slice(0, 10)}.mp3`;
            downloadLinkRef.current.href = audioUrlRef.current;
            downloadLinkRef.current.click();
          } else {
            toast({
              title: "Download failed",
              description: "Could not prepare audio for download",
              variant: "destructive"
            });
          }
        }, 1000); // Small delay to ensure audio processing is complete
      } catch (error) {
        toast({
          title: "Failed to generate audio",
          description: error instanceof Error ? error.message : "An unexpected error occurred",
          variant: "destructive"
        });
      }
      return;
    }
    
    // If audio already exists, download it directly
    if (downloadLinkRef.current && audioUrlRef.current) {
      downloadLinkRef.current.download = `Prayer-${new Date().toISOString().slice(0, 10)}.mp3`;
      downloadLinkRef.current.href = audioUrlRef.current;
      downloadLinkRef.current.click();
    }
  };

  // If audio is generating, show the loader
  if (isGeneratingAudio) {
    return <PrayerHeavenlyLoader message="Preparing your prayer audio..." />;
  }

  return (
    <div ref={scrollRef} className="mb-10">
      <Card className="border border-selah-gold/20 shadow-lg">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
            <h2 className="text-2xl font-playfair font-bold text-selah-navy">Your Prayer</h2>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
              {/* Audio Controls with improved props */}
              <AudioControls 
                isGeneratingAudio={isGeneratingAudio}
                isPlaying={isPlaying}
                isAudioReady={isAudioReady}
                error={audioError}
                isPrayerTooLong={isPrayerTooLong}
                selectedVoice={selectedVoice}
                loadingProgress={loadingProgress}
                isIOS={isIOS}
                onPlayPause={handlePlayPrayer}
              />
              
              {/* Prayer Actions component */}
              <PrayerActions 
                isGeneratingAudio={isGeneratingAudio}
                isAudioReady={isAudioReady}
                onSave={handleSavePrayer}
                onDownload={handleDownloadAudio}
              />
            </div>
          </div>
          
          {/* Prayer Text Component */}
          <PrayerText 
            prayer={result.prayer} 
            scriptures={result.scriptures} 
          />

          {/* Scripture References Component */}
          <ScriptureReferences scriptures={result.scriptures} />
        </CardContent>
      </Card>
      
      {/* Hidden download link */}
      <a 
        ref={downloadLinkRef} 
        style={{ display: 'none' }}
        href="/"
        download="prayer.mp3"
      />
    </div>
  );
};

export default PrayerResult;
